import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { getErrorString } from '../../utils';
import {
  fetchCommunicationSettingsApi,
  fetchLeadDetailApi,
  fetchLeadLabDocumentsApi,
  fetchLeadLabOrdersApi,
  fetchLeadOrdersApi,
  fetchLeadProductsApi,
  fetchLeadTicketsApi,
  fetchLeadUntrackedCasesApi,
} from '../../api/leads-api';
import { setJourneyStages } from '../user-dashboard-slice';
import {
  fetchCreditTransactionsApi,
  fetchPatientContactsApi,
  fetchPatientDetail,
  fetchPatientMeasurementsApi,
} from '../../api/patients-api';
import { fetchFollowUpsApi } from '../../api/follow-up-api';
import { getQueryString } from 'utils/filters-util';
import { fetchAddressesApi } from 'api/addresses-api';
import { fetchAvailableProductVariantsApi } from 'api/products-api';
import { fetchCheckoutSessionsApi } from 'api/checkout-sessions';

const initialState = {
  data: {},
  settings: {},
  productQuestionnaires: [],
  orders: [],
  products: [],
  contacts: [],
  measurements: {},
  untrackCases: [],
  checkoutSessions: [],
  patient: {},
  tickets: [],
  followUps: [],
  patient_addresses: [],
  credits: {},
  labOrders: [],
  availbleProducts: [],
  labDocuments: [],
  pagination: {},
  loading: { data: true, patient: true, communication: true },
};

export const fetchLeadDetails = createAsyncThunk('lead/details', async (id = '') => {
  try {
    const res = await fetchLeadDetailApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCommunicationSettings = createAsyncThunk('settings/communication', async (id = '') => {
  try {
    const res = await fetchCommunicationSettingsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadOrders = createAsyncThunk('lead/order-details', async ({ id = '', params = {}, dispatch }) => {
  try {
    const updatedParams = {
      page: 1,
      ...params,
    };
    const res = await fetchLeadOrdersApi(id, updatedParams);
    if (res?.status) {
      dispatch(setJourneyStages(res?.journey_stages || []));
    }
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchMoreLeadOrders = createAsyncThunk(
  'lead/more-order-details',
  async ({ id = '', params = {}, dispatch }) => {
    try {
      const updatedParams = {
        page: 1,
        ...params,
      };
      const res = await fetchLeadOrdersApi(id, updatedParams);
      if (res?.status) {
        dispatch(setJourneyStages(res?.journey_stages || []));
      }
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
    }
  },
);

export const fetchMoreLeadTickets = createAsyncThunk('lead/more-ticket-details', async ({ id = '', params = {} }) => {
  try {
    const updatedParams = {
      page: 1,
      ...params,
    };
    const res = await fetchLeadTicketsApi(id, '', updatedParams);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadLabOrders = createAsyncThunk('lead/lab-orders', async (id) => {
  try {
    const res = await fetchLeadLabOrdersApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadProducts = createAsyncThunk('lead/products', async (id) => {
  try {
    const res = await fetchLeadProductsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadUntrackedCases = createAsyncThunk('lead/cases-untracked', async (id = '') => {
  try {
    const res = await fetchLeadUntrackedCasesApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadCheckoutSessions = createAsyncThunk('lead/checkout-sessions', async ({ params = {} }) => {
  try {
    const updatedParams = {
      page: 1,
      ...params,
    };
    const res = await fetchCheckoutSessionsApi(updatedParams);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchMoreCheckoutSessions = createAsyncThunk('lead/more-checkout-sessions', async ({ params = {} }) => {
  try {
    const updatedParams = {
      page: 1,
      ...params,
    };
    const res = await fetchCheckoutSessionsApi(updatedParams);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadPatient = createAsyncThunk('lead/patient', async (id = '') => {
  try {
    const res = await fetchPatientDetail(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPatientContacts = createAsyncThunk('patient/contacts', async (id = '') => {
  try {
    const res = await fetchPatientContactsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPatientMeasurements = createAsyncThunk('patient/measurements', async (id = '') => {
  try {
    const res = await fetchPatientMeasurementsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPatientAddresses = createAsyncThunk('patient/addresses', async ({ patientId = ' ', params = {} }) => {
  try {
    const payload = {
      'q[patient_id_eq]': patientId,
      ...params,
    };
    const res = await fetchAddressesApi(getQueryString(payload));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPatientCredits = createAsyncThunk('patient/credits', async ({ id = ' ', params = {} }) => {
  try {
    const payload = {
      'f[lead.id][in]': id,
      ...params,
    };
    const res = await fetchCreditTransactionsApi({}, getQueryString(payload));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadTickets = createAsyncThunk('lead/tickets', async (id = '') => {
  try {
    const res = await fetchLeadTicketsApi(id, '', { per_page: 20 });
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadFollowUps = createAsyncThunk('lead/follow-ups', async (id = '') => {
  try {
    const res = await fetchFollowUpsApi({ per_page: 40, 'f[lead.id][eq]': id });
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadAvailableProductVariants = createAsyncThunk(
  'lead/available-product-variants',
  async ({ id = '', params = {} }) => {
    try {
      const paramsObj = {
        ...params,
        'q[available_eq]': true,
        'q[s]': 'created_at desc',
        'q[lead_id_eq]': id,
      };
      const res = await fetchAvailableProductVariantsApi(paramsObj);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
    }
  },
);

export const fetchExtraLeadAvialbleProductVariants = createAsyncThunk(
  'lead/available-product-variants/extra',
  async ({ id = '', params = {} }) => {
    try {
      const paramsObj = {
        ...params,
        'q[lead_id_eq]': id,
      };
      const res = await fetchAvailableProductVariantsApi(paramsObj);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
    }
  },
);

export const fetchLeadLabDocuments = createAsyncThunk('lead/lab-documents', async (id = '') => {
  try {
    const res = await fetchLeadLabDocumentsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

const LeadDetailsSlice = createSlice({
  name: 'leaddetails',
  initialState,
  reducers: {
    setLoader: (state, { payload }) => {
      state.loading[payload?.key] = payload?.value;
    },
    setData: (state, { payload }) => {
      state.data = payload;
    },
    updateData: (state, { payload }) => {
      state.data[payload?.key] = payload?.value;
    },
    setTicketUpdate: (state, { payload }) => {
      state.tickets = state?.tickets?.map((ticket) => (ticket?.id === payload?.id ? payload : ticket));
    },
    setContacts: (state, { payload }) => {
      state.contacts = payload;
    },
    setProductQuestionnaires: (state, { payload }) => {
      state.productQuestionnaires = payload;
    },
    setOrders: (state, { payload }) => {
      state.orders = [...payload];
    },
    setPatientAddresses: (state, { payload }) => {
      state.patient_addresses = [...payload];
    },
    setPatientData: (state, { payload }) => {
      state.patient = payload;
    },
    updatePatientData: (state, { payload }) => {
      state.patient = { ...state.patient, ...payload };
    },
    setCheckoutSessionUpdate: (state, { payload }) => {
      state.checkoutSessions = state?.checkoutSessions?.map((session) =>
        session?.id === payload?.id ? payload : session,
      );
    },
    setLabOrdersAdd: (state, { payload }) => {
      state.labOrders.unshift(payload);
    },
    setLabDocuments: (state, { payload }) => {
      state.labDocuments = payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLeadDetails.pending, (state) => {
        state.loading.data = true;
      })
      .addCase(fetchLeadDetails.fulfilled, (state, action) => {
        state.data = action?.payload?.data || [];
        state.loading.data = false;
      })
      .addCase(fetchLeadDetails.rejected, (state) => {
        state.loading.data = false;
      })
      .addCase(fetchLeadOrders.pending, (state) => {
        state.loading.orders = true;
      })
      .addCase(fetchLeadOrders.fulfilled, (state, action) => {
        const pagination = action?.payload?.meta?.pagination ?? {};
        if (pagination?.page === 1) {
          // First page, replace all orders
          state.orders = action?.payload?.data || [];
        } else {
          // Subsequent pages, append to existing orders
          state.orders = action?.payload?.data ? [...state.orders, ...action.payload.data] : [...state.orders];
        }
        state.productQuestionnaires = action?.payload?.product_questionnaires || [];
        state.loading.orders = false;
        state.pagination.orders = action?.payload?.meta?.pagination ?? {};
      })
      .addCase(fetchLeadOrders.rejected, (state) => {
        state.loading.orders = false;
      })
      .addCase(fetchMoreLeadOrders.pending, (state) => {
        state.loading.loadMoreOrders = true;
      })
      .addCase(fetchMoreLeadOrders.fulfilled, (state, action) => {
        // Subsequent pages, append to existing orders
        state.orders = action?.payload?.data ? [...state.orders, ...action.payload.data] : [...state.orders];

        state.productQuestionnaires = action?.payload?.product_questionnaires || [];
        state.loading.loadMoreOrders = false;
        state.pagination.orders = action?.payload?.meta?.pagination ?? {};
      })
      .addCase(fetchMoreLeadOrders.rejected, (state) => {
        state.loading.loadMoreOrders = false;
      })
      .addCase(fetchMoreLeadTickets.pending, (state) => {
        state.loading.loadMoreTickets = true;
      })
      .addCase(fetchMoreLeadTickets.fulfilled, (state, action) => {
        state.tickets = action?.payload?.data ? [...state.tickets, ...action.payload.data] : [...state.tickets];
        state.loading.loadMoreTickets = false;
        state.pagination.tickets = action?.payload?.meta?.pagination ?? {};
      })
      .addCase(fetchMoreLeadTickets.rejected, (state) => {
        state.loading.loadMoreTickets = false;
      })
      .addCase(fetchLeadUntrackedCases.pending, (state) => {
        state.loading.untrackCases = true;
      })
      .addCase(fetchLeadUntrackedCases.fulfilled, (state, action) => {
        state.untrackCases = action?.payload?.data || [];
        state.loading.untrackCases = false;
      })
      .addCase(fetchLeadUntrackedCases.rejected, (state) => {
        state.loading.untrackCases = false;
      })
      .addCase(fetchLeadCheckoutSessions.pending, (state) => {
        state.loading.checkoutSessions = true;
      })
      .addCase(fetchLeadCheckoutSessions.fulfilled, (state, action) => {
        const pagination = action?.payload?.meta?.pagination ?? {};
        if (pagination?.page === 1) {
          state.checkoutSessions = action?.payload?.data || [];
        } else {
          state.checkoutSessions = action?.payload?.data
            ? [...state.checkoutSessions, ...action.payload.data]
            : [...state.checkoutSessions];
        }
        state.loading.checkoutSessions = false;
        state.pagination.checkoutSessions = action?.payload?.meta?.pagination ?? {};
      })
      .addCase(fetchLeadCheckoutSessions.rejected, (state) => {
        state.loading.checkoutSessions = false;
      })

      .addCase(fetchMoreCheckoutSessions.pending, (state) => {
        state.loading.loadMoreCheckoutSessions = true;
      })
      .addCase(fetchMoreCheckoutSessions.fulfilled, (state, action) => {
        state.checkoutSessions = action?.payload?.data
          ? [...state.checkoutSessions, ...action.payload.data]
          : [...state.checkoutSessions];

        state.loading.loadMoreCheckoutSessions = false;
        state.pagination.checkoutSessions = action?.payload?.meta?.pagination ?? {};
      })
      .addCase(fetchMoreCheckoutSessions.rejected, (state) => {
        state.loading.loadMoreCheckoutSessions = false;
      })

      .addCase(fetchLeadPatient.pending, (state) => {
        state.loading.patient = true;
      })
      .addCase(fetchLeadPatient.fulfilled, (state, action) => {
        state.patient = action?.payload?.data || [];
        state.loading.patient = false;
      })
      .addCase(fetchLeadPatient.rejected, (state) => {
        state.loading.patient = false;
      })
      .addCase(fetchLeadTickets.pending, (state) => {
        state.loading.tickets = true;
      })
      .addCase(fetchLeadTickets.fulfilled, (state, action) => {
        state.tickets = action?.payload?.data || [];
        state.pagination.tickets = action?.payload?.meta?.pagination ?? {};
        state.loading.tickets = false;
      })
      .addCase(fetchLeadTickets.rejected, (state) => {
        state.loading.tickets = false;
      })
      .addCase(fetchLeadFollowUps.pending, (state) => {
        state.loading.followUps = true;
      })
      .addCase(fetchLeadFollowUps.fulfilled, (state, action) => {
        state.followUps = action?.payload?.data || [];
        state.loading.followUps = false;
      })
      .addCase(fetchLeadFollowUps.rejected, (state) => {
        state.loading.followUps = false;
      })
      .addCase(fetchPatientContacts.pending, (state) => {
        state.loading.contacts = true;
      })
      .addCase(fetchPatientContacts.fulfilled, (state, action) => {
        state.contacts = action?.payload?.data || [];
        state.loading.contacts = false;
      })
      .addCase(fetchPatientContacts.rejected, (state) => {
        state.loading.contacts = false;
      })
      .addCase(fetchPatientMeasurements.pending, (state, action) => {
        if (action?.meta?.arg !== state.measurements?.patient?.id) {
          state.loading.measurements = true;
        }
      })
      .addCase(fetchPatientMeasurements.fulfilled, (state, action) => {
        state.measurements = action?.payload || {};
        state.loading.measurements = false;
      })
      .addCase(fetchPatientMeasurements.rejected, (state) => {
        state.loading.measurements = false;
      })
      .addCase(fetchLeadProducts.pending, (state) => {
        state.loading.products = true;
      })
      .addCase(fetchLeadProducts.fulfilled, (state, action) => {
        state.products = action?.payload?.data || [];
        state.loading.products = false;
      })
      .addCase(fetchLeadProducts.rejected, (state) => {
        state.loading.products = false;
      })
      .addCase(fetchCommunicationSettings.pending, (state) => {
        state.loading.communication = true;
      })
      .addCase(fetchCommunicationSettings.fulfilled, (state, action) => {
        state.settings = action?.payload || {};
        state.loading.communication = false;
      })
      .addCase(fetchCommunicationSettings.rejected, (state) => {
        state.loading.communication = false;
      })
      .addCase(fetchPatientAddresses.pending, (state) => {
        state.loading.addresses = true;
      })
      .addCase(fetchPatientAddresses.fulfilled, (state, action) => {
        state.loading.addresses = false;
        state.patient_addresses = action?.payload?.data || [];
      })
      .addCase(fetchPatientAddresses.rejected, (state) => {
        state.loading.addresses = false;
      })

      .addCase(fetchPatientCredits.pending, (state) => {
        state.loading.credits = true;
      })
      .addCase(fetchPatientCredits.fulfilled, (state, action) => {
        state.loading.credits = false;
        const pagination = action?.payload?.meta?.pagination ?? {};
        if (pagination?.page === 1) {
          state.credits = action?.payload || {};
        } else {
          state.credits = {
            ...(action?.payload || {}),
            data: [...(state.credits?.data || []), ...(action?.payload?.data || [])],
          };
        }
      })
      .addCase(fetchPatientCredits.rejected, (state) => {
        state.loading.credits = false;
      })

      .addCase(fetchLeadLabOrders.pending, (state) => {
        state.loading.labOrders = true;
      })
      .addCase(fetchLeadLabOrders.fulfilled, (state, action) => {
        state.loading.labOrders = false;
        state.labOrders = action?.payload?.data ?? [];
      })
      .addCase(fetchLeadLabOrders.rejected, (state) => {
        state.loading.labOrders = false;
      })
      .addCase(fetchLeadAvailableProductVariants.pending, (state) => {
        state.loading.availbleProducts = state.pagination.availbleProducts?.page ? false : true;
        state.loading.availbleProductsMore = true;
      })
      .addCase(fetchLeadAvailableProductVariants.fulfilled, (state, action) => {
        const pagination = action?.payload?.meta?.pagination ?? {};
        if (pagination?.page === 1) {
          state.availbleProducts = action?.payload?.data || [];
        } else {
          state.orders = action?.payload?.data ? [...state.orders, ...action.payload.data] : [...state.orders];
          state.availbleProducts = [...state.availbleProducts, ...(action?.payload?.data || [])];
        }
        state.loading.availbleProducts = false;
        state.loading.availbleProductsMore = false;
        state.pagination.availbleProducts = action?.payload?.meta?.pagination;
      })
      .addCase(fetchLeadAvailableProductVariants.rejected, (state) => {
        state.loading.availbleProducts = false;
        state.loading.availbleProductsMore = false;
      })
      .addCase(fetchLeadLabDocuments.pending, (state) => {
        state.loading.labDocuments = true;
      })
      .addCase(fetchLeadLabDocuments.fulfilled, (state, action) => {
        state.labDocuments = action?.payload?.data || [];
        state.loading.labDocuments = false;
      })
      .addCase(fetchLeadLabDocuments.rejected, (state) => {
        state.loading.labDocuments = false;
      });
  },
});

export const {
  setLoader,
  setData,
  updateData,
  setTicketUpdate,
  setContacts,
  setProductQuestionnaires,
  setOrders,
  setPatientData,
  updatePatientData,
  setCheckoutSessionUpdate,
  setLabOrdersAdd,
  setLabDocuments,
} = LeadDetailsSlice.actions;
export default LeadDetailsSlice.reducer;
